import { get, post, put } from "api/http";

import { SERVICES_URL, handleError } from "api/helper";
import { CampaignService } from "@adfenix/types";
import { CustomerFacingCampaignStatus } from "types/CampaignStatus";

const baseAuthed = "campaign/auth/v1";
const baseAuthedV2 = "campaign/auth/v2";

export const getCampaignDetails = (
  campaignId: string
): Promise<CampaignService.CampaignDetailsResponse> =>
  get(`${SERVICES_URL}/${baseAuthedV2}/campaigns/${campaignId}`).catch(
    handleError
  );

export const retryCampaign = ({
  campaignId,
  adCreative
}: {
  campaignId: string;
  adCreative: CampaignService.AdCreativeModel | null;
}): Promise<void> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/campaigns/${campaignId}/retry`,
    adCreative
  ).catch(handleError);

export const reOrderCampaign = (
  request: CampaignService.ReorderMetaCampaignRequest
): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthedV2}/campaigns/reorder-meta-campaign`,
    request
  ).catch(handleError);

export const reOrderCampaignV2 = ({
  campaignTemplateId,
  campaignId
}: {
  campaignTemplateId: string;
  campaignId: string;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthedV2}/campaigns/${campaignId}/reorder-facebook-campaign/${campaignTemplateId}`
  );

export const stopCampaign = ({
  campaignId
}: {
  campaignId: string;
}): Promise<void> =>
  post(`${SERVICES_URL}/${baseAuthed}/campaigns/${campaignId}/stop`).catch(
    handleError
  );

export const getCampaignStatus = (
  propertyId: string
): Promise<CustomerFacingCampaignStatus> =>
  get(`${SERVICES_URL}/${baseAuthed}/status/${propertyId}`).catch(handleError);

export const getCampaignResults = (
  subjectId: string
): Promise<CampaignService.ResultsV2Model> =>
  get(`${SERVICES_URL}/${baseAuthedV2}/result/${subjectId}`).catch(handleError);

export const removeInstagram = ({
  campaignId
}: {
  campaignId: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${baseAuthed}/campaigns/${campaignId}/remove-instagram`
  ).catch(handleError);

export const getFacebookAdCampaignMessageSetting = (
  campaignId: string
): Promise<CampaignService.FacebookAdMessageSettings> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/facebook-ad-campaign/${campaignId}/message-settings`
  ).catch(handleError);

export const getDisplayAdCampaignMessageSetting = (
  campaignId: string
): Promise<CampaignService.DisplayAdMessageSettings> =>
  get(
    `${SERVICES_URL}/${baseAuthed}/display-ad-campaign/${campaignId}/message-settings`
  ).catch(handleError);

export const updateFacebookAdMessageSetting = ({
  campaignId,
  sendStatisticsEmail,
  sendCampaignLiveEmail
}: {
  campaignId: string;
  sendStatisticsEmail: boolean;
  sendCampaignLiveEmail: boolean;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/facebook-ad-campaign/${campaignId}/message-settings`,
    {
      sendStatisticsEmail: sendStatisticsEmail,
      sendCampaignLiveEmail: sendCampaignLiveEmail
    }
  ).catch(handleError);

export const updateDisplayAdMessageSetting = ({
  campaignId,
  sendStatisticsEmail
}: {
  campaignId: string;
  sendStatisticsEmail: boolean;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/display-ad-campaign/${campaignId}/message-settings`,
    {
      sendStatisticsEmail: sendStatisticsEmail
    }
  ).catch(handleError);

export const updateFacebookAdLandingPage = ({
  campaignId,
  landingPageUrl
}: {
  campaignId: string;
  landingPageUrl: string;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/facebook-ad-campaign/${campaignId}/landing-page-settings`,
    {
      landingPageUrl,
      hasCustomLandingPage: true
    }
  ).catch(handleError);

export const updateDisplayAdLandingPage = ({
  campaignId,
  landingPageUrl
}: {
  campaignId: string;
  landingPageUrl: string;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/display-ad-campaign/${campaignId}/landing-page-settings`,
    {
      landingPageUrl,
      hasCustomLandingPage: true
    }
  ).catch(handleError);

export const updateFacebookAdLocation = ({
  campaignId,
  coordinates
}: {
  campaignId: string;
  coordinates: CampaignService.Coordinates;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/facebook-ad-campaign/${campaignId}/location-settings`,
    {
      coordinates,
      hasCustomLandingPage: true
    }
  ).catch(handleError);

export const updateDisplayAdLocation = ({
  campaignId,
  coordinates
}: {
  campaignId: string;
  coordinates: CampaignService.Coordinates;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/display-ad-campaign/${campaignId}/location-settings`,
    {
      coordinates,
      hasCustomLandingPage: true
    }
  ).catch(handleError);

export const updateFacebookDni = ({
  model,
  campaignId
}: {
  model: CampaignService.DoNotInvoiceSettings;
  campaignId: string;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/facebook-ad-campaign/${campaignId}/do-not-invoice`,
    model
  ).catch(handleError);

export const updateDisplayAdDni = ({
  model,
  campaignId
}: {
  model: CampaignService.DoNotInvoiceSettings;
  campaignId: string;
}): Promise<string> =>
  post(
    `${SERVICES_URL}/${baseAuthed}/display-ad-campaign/${campaignId}/do-not-invoice`,
    model
  ).catch(handleError);

export const getCampaignStatuses = ({
  orderId,
  bundleId
}: {
  orderId: string;
  bundleId: string;
}): Promise<CampaignService.CampaignStatusModel[]> =>
  get(`${SERVICES_URL}/${baseAuthed}/campaigns/${orderId}/${bundleId}`).catch(
    handleError
  );
